import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import MainNav from "../../components/MainNav";
import SiteFooter from "../../components/SiteFooter";
import ServiceVideosSection from "../../components/ServiceVideosSection";
import MagazineSpotlight from "../../components/MagazineSpotlight";
import AnnualCatalogSection from "../../components/AnnualCatalogSection";
import {
  Wine, Sparkles, TrendingUp, FileText, ArrowRight,
  Building2, Tag, ShoppingBag, ListChecks, Megaphone, Globe, ShieldCheck,
  Tv, Brain, Ship, Languages, Handshake, MapPin, Check, Award,
  Utensils, BookOpen, ChefHat, BookMarked, Newspaper, ArrowUpRight,
} from "lucide-react";

/* ========================================================================
   B2B HOMEPAGE — Amazonico 3000 · Wine + Food + Media + Export
   Beige/Gold editorial theme, category grid, monthly magazine,
   annual catalog, recipes & pairings, moving image marquees.
   Inspired by epicurefoodscorp.com structure, elevated to Italian luxury.
   ======================================================================== */

// Pexels food/wine imagery
const IMG = {
  hero: "https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg",
  heroAlt: "https://images.pexels.com/photos/2440529/pexels-photo-2440529.jpeg",
  wine: "https://images.pexels.com/photos/34736555/pexels-photo-34736555.jpeg",
  cheese: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=1200&q=80",
  charcuterie: "https://images.unsplash.com/photo-1541529086526-db283c563270?w=1200&q=80",
  pasta: "https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg",
  oliveOil: "https://images.pexels.com/photos/33783/olive-oil-salad-dressing-cooking-olive.jpg",
  chocolate: "https://images.pexels.com/photos/236259/pexels-photo-236259.jpeg",
  coffee: "https://images.pexels.com/photos/1695052/pexels-photo-1695052.jpeg",
  caviar: "https://images.unsplash.com/photo-1580476262798-bddd9f4b7369?w=1200&q=80",
  tuna: "https://images.pexels.com/photos/2871757/pexels-photo-2871757.jpeg",
  dessert: "https://images.pexels.com/photos/34759482/pexels-photo-34759482.jpeg",
  truffle: "https://images.pexels.com/photos/2440529/pexels-photo-2440529.jpeg",
  bread: "https://images.pexels.com/photos/1775043/pexels-photo-1775043.jpeg",
  magazine: "https://images.pexels.com/photos/1407846/pexels-photo-1407846.jpeg",
  catalog: "https://images.pexels.com/photos/36430249/pexels-photo-36430249.jpeg",
  recipes: "https://images.pexels.com/photos/725991/pexels-photo-725991.jpeg",
  vineyards: "https://images.pexels.com/photos/2440529/pexels-photo-2440529.jpeg",
  about: "https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg",
  blog: "https://images.pexels.com/photos/29609013/pexels-photo-29609013.jpeg",
};

export default function B2BHome({ hideNav = false }) {
  return (
    <div data-testid="b2b-home-page" className="bg-[#f7f1e3] text-stone-900">
      {!hideNav && <MainNav />}
      <Hero />
      <MarqueeStrip />
      <CategoriesGrid />
      <MagazineSpotlight />
      <AnnualCatalogSection />
      <ServiceVideosSection />
      <MovingShowcase />
      <FeatureCards />
      <EcosystemBand />
      <BrazilFocus />
      <PlansSection />
      <ToolsBand />
      <FinalCta />
      {!hideNav && <SiteFooter />}
    </div>
  );
}

/* ========================= HERO WITH CAROUSEL ========================= */
const HERO_SLIDES = [
  {
    img: "https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg",
    eyebrow: "B2B ExportTech · Wine · Food · Media",
    title: <>L&apos;eccellenza <span className="italic text-[#A88A4A]">agroalimentare</span><br />italiana,<br /><span className="italic text-[#A88A4A]">in un solo portale.</span></>,
    subtitle: "Vini · Formaggi · Salumi · Olio EVO · Pasta · Dolci · Caffè · Tartufo. Marketplace, AI e servizi export in un unico ecosistema.",
  },
  {
    img: "https://images.pexels.com/photos/34736555/pexels-photo-34736555.jpeg",
    eyebrow: "Marketplace B2B · 1.200+ etichette · 11 paesi",
    title: <>Le grandi <span className="italic text-[#A88A4A]">cantine italiane</span><br />vendono con noi.</>,
    subtitle: "DOCG, DOC, IGT, Metodo Classico. Buyer verificati in 10+ paesi, pricing dinamico e preventivi PDF in un click.",
  },
  {
    img: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?w=1920&q=80",
    eyebrow: "Filiere Food · 8 categorie premium",
    title: <>Il gusto <span className="italic text-[#A88A4A]">Made in Italy</span><br />per il mondo.</>,
    subtitle: "Parmigiano, Prosciutto di Parma, Pasta artigianale, Olio EVO IGP, Cioccolato di Modica, Tartufo. Selezionati per l&apos;export premium.",
  },
  {
    img: "https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg",
    eyebrow: "Brazil Desk · San Paolo",
    title: <>Da Milano <span className="italic text-[#A88A4A]">a San Paolo</span>,<br />senza attriti.</>,
    subtitle: "Team commerciale locale, compliance MAPA/ANVISA, container reefer, network di importatori consolidati.",
  },
  {
    img: "https://images.pexels.com/photos/29609013/pexels-photo-29609013.jpeg",
    eyebrow: "Rivista mensile · Amazonico Magazine",
    title: <>Non solo B2B.<br /><span className="italic text-[#A88A4A]">Un editore digitale.</span></>,
    subtitle: "Ogni mese un nuovo numero: promozioni, storytelling produttori, ricette, tendenze di mercato.",
  },
];

function Hero() {
  const [idx, setIdx] = React.useState(0);
  const total = HERO_SLIDES.length;

  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 6000);
    return () => clearInterval(t);
  }, [total]);

  const slide = HERO_SLIDES[idx];

  return (
    <section data-testid="hero-carousel" className="relative min-h-[92vh] flex items-end overflow-hidden bg-gradient-to-b from-[#faf7f0] via-[#f7f1e3] to-[#efe6d0]">
      {/* Slides layer with cross-fade */}
      <div className="absolute inset-0 overflow-hidden">
        {HERO_SLIDES.map((s, i) => (
          <img
            key={i}
            src={s.img}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover will-change-transform animate-[kenburns_18s_ease-in-out_infinite] transition-opacity duration-[1400ms] ${i === idx ? "opacity-40" : "opacity-0"}`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f0]/60 via-[#f7f1e3]/40 to-[#f7f1e3]" />
        <div className="absolute inset-0 opacity-60" style={{ backgroundImage: "radial-gradient(circle at 15% 20%, rgba(201,169,97,0.35) 0%, transparent 55%)" }} />
      </div>

      <div className="relative container-xl w-full pb-16 md:pb-24 pt-32">
        <div className="max-w-4xl">
          <div key={`eyebrow-${idx}`} className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A961]/60 bg-white/60 backdrop-blur mb-6 animate-[fadeInUp_800ms_ease-out]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A961] animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold">{slide.eyebrow}</span>
          </div>
          <h1 key={`title-${idx}`} className="editorial-heading text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] text-stone-900 animate-[fadeInUp_900ms_ease-out]">
            {slide.title}
          </h1>
          <p key={`sub-${idx}`} className="mt-8 text-lg md:text-xl text-stone-600 max-w-3xl leading-relaxed animate-[fadeInUp_1000ms_ease-out]">
            {slide.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/register" data-testid="b2b-cta-partner">
              <Button size="lg" className="bg-[#C9A961] hover:bg-[#A88A4A] text-white gap-2 rounded-none px-8 h-14 text-sm uppercase tracking-[0.2em] font-bold">
                <Handshake className="h-4 w-4" /> Diventa Partner
              </Button>
            </Link>
            <Link to="/b2b/wines" data-testid="b2b-cta-catalog">
              <Button size="lg" variant="outline" className="border-stone-900/40 text-stone-900 bg-white/40 hover:bg-stone-900 hover:text-white gap-2 rounded-none px-8 h-14 text-sm uppercase tracking-[0.2em] font-bold backdrop-blur">
                <ShoppingBag className="h-4 w-4" /> Esplora Catalogo
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            {[
              { v: "€50B", l: "Export agroalim. IT 2024" },
              { v: "70K+", l: "Aziende esportatrici" },
              { v: "210M", l: "Consumatori BR" },
              { v: "10+", l: "Paesi con buyer" },
            ].map((s, i) => (
              <div key={i}>
                <div className="editorial-heading text-3xl md:text-4xl text-[#A88A4A] leading-none">{s.v}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-2">{s.l}</div>
              </div>
            ))}
          </div>

          {/* Carousel dots */}
          <div className="mt-10 flex items-center gap-3">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`hero-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                className={`h-1 transition-all duration-500 ${i === idx ? "w-10 bg-[#A88A4A]" : "w-6 bg-stone-400/60 hover:bg-stone-500"}`}
              />
            ))}
            <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-stone-500 font-bold">{String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes kenburns {
          0%, 100% { transform: scale(1.08) translate(0,0); }
          50% { transform: scale(1.2) translate(-2%, -1%); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

/* ========================= MARQUEE STRIP ========================= */
function MarqueeStrip() {
  const items = [
    "◆ Marketplace B2B live 24/7",
    "◆ AI matching predittivo",
    "◆ Rivista mensile digitale",
    "◆ 8 filiere agroalimentari",
    "◆ Brazil Desk operativo",
    "◆ Certificazioni MAPA / ANVISA",
    "◆ Video Amazonico TV",
    "◆ Ricette & abbinamenti",
  ];
  return (
    <div className="border-y border-[#C9A961]/30 bg-gradient-to-r from-[#C9A961] via-[#e6c67e] to-[#C9A961] overflow-hidden">
      <div className="flex gap-12 py-3.5 whitespace-nowrap animate-[marquee_35s_linear_infinite]">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="text-[11px] uppercase tracking-[0.35em] text-white font-bold shrink-0">{t}</span>
        ))}
      </div>
      <style>{`@keyframes marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-33.33%)} }`}</style>
    </div>
  );
}

/* ========================= CATEGORIES GRID (Epicure-inspired) ========================= */
function CategoriesGrid() {
  const cats = [
    { name: "Vini & Bollicine", img: IMG.wine, desc: "DOCG · DOC · IGT · Metodo Classico", link: "/b2b/wines" },
    { name: "Formaggi", img: IMG.cheese, desc: "Parmigiano · Pecorino · Bufala", link: "/b2b/wines" },
    { name: "Salumi & Charcuterie", img: IMG.charcuterie, desc: "Parma · Bresaola · Salame DOP", link: "/b2b/wines" },
    { name: "Olio EVO & Aceti", img: IMG.oliveOil, desc: "IGP · DOP · Balsamici Modena", link: "/b2b/wines" },
    { name: "Pasta & Cereali", img: IMG.pasta, desc: "Artigianale · Trafilato al bronzo", link: "/b2b/wines" },
    { name: "Dolci & Pasticceria", img: IMG.dessert, desc: "Cioccolato · Biscotti · Confetture", link: "/b2b/wines" },
    { name: "Caffè & Beverage", img: IMG.coffee, desc: "Espresso · Capsule · Miscele", link: "/b2b/wines" },
    { name: "Tartufo & Delicatessen", img: IMG.truffle, desc: "Bianco · Nero · Conserve gourmet", link: "/b2b/wines" },
    { name: "Caviale & Roe", img: IMG.caviar, desc: "Ossetra · Beluga · Delicatessen premium", link: "/b2b/wines" },
    { name: "Tonno & Pesce Pregiato", img: IMG.tuna, desc: "Tonno rosso · Bottarga · Alici Cetara", link: "/b2b/wines" },
  ];
  return (
    <section id="categorie" className="py-24 md:py-32 bg-[#faf7f0]" data-testid="b2b-categories">
      <div className="container-xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold mb-3">Le nostre filiere</div>
            <h2 className="editorial-heading text-4xl md:text-6xl text-stone-900 leading-[1.05]">
              Otto categorie,<br />
              <span className="italic text-[#A88A4A]">un&apos;unica eccellenza.</span>
            </h2>
          </div>
          <p className="text-sm text-stone-600 max-w-md leading-relaxed">
            Ogni filiera è curata con selezione produttori, storytelling territoriale, certificazioni export e schede tecniche multilingua per il mercato brasiliano e sudamericano.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cats.map((c, i) => (
            <Link
              key={i}
              to={c.link}
              data-testid={`cat-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group relative aspect-[3/4] overflow-hidden bg-stone-200"
            >
              <img
                src={c.img}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute inset-0 border border-[#C9A961]/0 group-hover:border-[#C9A961] transition-colors duration-300" style={{ margin: 12 }} />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <h3 className="editorial-heading text-2xl text-white leading-tight">{c.name}</h3>
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/80 mt-2">{c.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold opacity-90 group-hover:gap-2.5 transition-all">
                  Sfoglia selezione <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================= MOVING SHOWCASE — continuous image marquee ========================= */
function MovingShowcase() {
  const gallery = [
    { img: IMG.wine, label: "Barolo DOCG" },
    { img: IMG.cheese, label: "Parmigiano Reggiano" },
    { img: IMG.pasta, label: "Trafilata al bronzo" },
    { img: IMG.oliveOil, label: "EVO Toscano IGP" },
    { img: IMG.charcuterie, label: "Prosciutto Parma" },
    { img: IMG.truffle, label: "Tartufo bianco" },
    { img: IMG.chocolate, label: "Cioccolato Modica" },
    { img: IMG.dessert, label: "Tiramisù Veneto" },
    { img: IMG.coffee, label: "Espresso italiano" },
    { img: IMG.bread, label: "Pane artigianale" },
  ];
  return (
    <section className="relative py-20 overflow-hidden bg-gradient-to-r from-[#C9A961] via-[#d9b876] to-[#C9A961]">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.4) 0%, transparent 70%)" }} />
      <div className="relative">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white font-bold mb-3">Il gusto in movimento</div>
          <h2 className="editorial-heading text-3xl md:text-5xl text-white leading-[1.05]">
            Il <span className="italic">Made in Italy</span> come non lo hai mai visto.
          </h2>
        </div>

        {/* Top row moving right */}
        <div className="flex gap-4 mb-4 whitespace-nowrap animate-[marqueeSlow_50s_linear_infinite]">
          {[...gallery, ...gallery].map((g, i) => (
            <div key={i} className="relative w-64 h-40 flex-shrink-0 overflow-hidden border-2 border-white/30 shadow-lg">
              <img src={g.img} alt={g.label} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-3 left-3 text-white text-xs uppercase tracking-[0.2em] font-bold">{g.label}</div>
            </div>
          ))}
        </div>
        {/* Bottom row moving left */}
        <div className="flex gap-4 whitespace-nowrap animate-[marqueeReverse_45s_linear_infinite]">
          {[...gallery.slice().reverse(), ...gallery.slice().reverse()].map((g, i) => (
            <div key={i} className="relative w-64 h-40 flex-shrink-0 overflow-hidden border-2 border-white/30 shadow-lg">
              <img src={g.img} alt={g.label} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-3 left-3 text-white text-xs uppercase tracking-[0.2em] font-bold">{g.label}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes marqueeSlow { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes marqueeReverse { 0%{transform:translateX(-50%)} 100%{transform:translateX(0)} }
      `}</style>
    </section>
  );
}

/* ========================= FEATURE CARDS · Ricette + About + Blog ========================= */
function FeatureCards() {
  const cards = [
    {
      to: "/ai-ricette",
      testid: "feature-recipes",
      badge: "AI Sommelier · GPT-5.2",
      eyebrow: "Ricette & Abbinamenti",
      title: "Cucina italiana × AI Sommelier",
      body: "Ricette autentiche italiane con abbinamento vino generato dall'AI. Per chef, Horeca, importatori e appassionati.",
      cta: "Esplora ricette",
      img: IMG.recipes,
    },
    {
      to: "/il-progetto",
      testid: "feature-about",
      badge: "Chi siamo · Il Progetto",
      eyebrow: "La storia",
      title: "La storia di Amazonico 3000",
      body: "Scopri chi siamo e il progetto ExportTech che collega Italia e Brasile con marketplace, AI e presenza locale.",
      cta: "Il progetto",
      img: IMG.about,
    },
    {
      to: "/magazine",
      testid: "feature-blog",
      badge: "Blog & Insights",
      eyebrow: "Cultura del cibo",
      title: "Cultura del cibo & mercati",
      body: "Articoli, interviste produttori, analisi export, tendenze del settore agroalimentare mondiale.",
      cta: "Vai al blog",
      img: IMG.blog,
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-[#f7f1e3]" data-testid="b2b-features">
      <div className="container-xl">
        <div className="text-center mb-16">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold mb-4">Approfondisci</div>
          <h2 className="editorial-heading text-4xl md:text-6xl text-stone-900 leading-[1.05]">
            Il mondo <span className="italic text-[#A88A4A]">Amazonico 3000</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {cards.map((c) => (
            <Link
              key={c.testid}
              to={c.to}
              data-testid={c.testid}
              className="group relative bg-white border border-[#C9A961]/30 hover:border-[#C9A961] overflow-hidden transition-all hover:shadow-2xl hover:-translate-y-1 duration-500"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#C9A961] text-white text-[9px] uppercase tracking-[0.25em] font-bold">
                  {c.badge}
                </div>
              </div>
              <div className="p-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold mb-3">{c.eyebrow}</div>
                <h3 className="editorial-heading text-2xl md:text-3xl text-stone-900 leading-tight">{c.title}</h3>
                <p className="mt-4 text-sm text-stone-600 leading-relaxed">{c.body}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#A88A4A] font-bold group-hover:gap-3 transition-all">
                  {c.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================= ECOSYSTEM BAND ========================= */
function EcosystemBand() {
  const pillars = [
    { icon: ShoppingBag, title: "Marketplace B2B", text: "Vetrina 24/7 con prezzi per cartone/pallet, MOQ, incoterms.", link: "/b2b/wines" },
    { icon: Brain, title: "Amazonico AI", text: "Matching buyer↔prodotto, forecast stock, marketing predittivo, AI sommelier.", link: "/b2b/sommelier" },
    { icon: Ship, title: "Export Services", text: "Dogana, MAPA/ANVISA, logistica end-to-end, container reefer consolidati.", link: "/gestionale" },
    { icon: Tv, title: "Amazonico TV", text: "Storytelling video: interviste, degustazioni, QR experience per vendere emozione.", link: "/tv" },
    { icon: Megaphone, title: "Marketing", text: "Campagne social, SEM, email, traduzioni professionali in 6 lingue.", link: "/b2b/marketing" },
    { icon: MapPin, title: "Brazil Desk", text: "Presenza locale a San Paolo, team commerciale, eventi tasting, network importatori.", link: "/gestionale/crm-leads" },
  ];
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a0608] via-[#2a0d10] to-[#1a0608]" />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,97,0.35) 0%, transparent 55%), radial-gradient(circle at 80% 70%, rgba(201,169,97,0.25) 0%, transparent 55%)" }} />
      <div className="relative container-xl">
        <div className="text-center mb-16">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-4">Ecosistema ExportTech</div>
          <h2 className="editorial-heading text-4xl md:text-6xl text-white leading-[1.05]">
            Sei pilastri.<br />
            <span className="italic text-[#C9A961]">Un solo abbonamento.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pillars.map((p, i) => (
            <Link key={i} to={p.link} className="group relative bg-white/[0.03] border border-[#C9A961]/20 hover:border-[#C9A961]/70 p-8 backdrop-blur-sm hover:bg-white/[0.06] transition-all overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#C9A961]/10 rounded-full blur-3xl group-hover:bg-[#C9A961]/25 transition-all" />
              <p.icon className="h-8 w-8 text-[#C9A961] mb-5" strokeWidth={1.5} />
              <h3 className="editorial-heading text-2xl text-white leading-tight">{p.title}</h3>
              <p className="text-sm text-white/70 mt-3 leading-relaxed">{p.text}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold group-hover:gap-3 transition-all">
                Scopri <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================= BRAZIL FOCUS ========================= */
function BrazilFocus() {
  return (
    <section className="py-24 md:py-32 bg-[#faf7f0]" data-testid="b2b-brazil">
      <div className="container-xl">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-8 h-6 bg-[#009c3b]" title="Brasile" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold">Focus mercato</span>
            </div>
            <h2 className="editorial-heading text-4xl md:text-6xl text-stone-900 leading-[1.05]">
              Il Brasile è la nostra<br />
              <span className="italic text-[#A88A4A]">porta d&apos;ingresso al Sud America.</span>
            </h2>
            <p className="mt-6 text-stone-600 text-lg leading-relaxed">
              210 milioni di consumatori, classe media in crescita, appetito per il Made in Italy premium.
              Il nostro <b className="text-stone-900">Brazil Desk</b> a San Paolo ti accompagna con team commerciale locale,
              supporto MAPA/ANVISA, eventi tasting e network di importatori consolidati.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                { v: "210M", l: "Consumatori BR" },
                { v: "+22%", l: "Import agroalim." },
                { v: "8", l: "Regioni coperte" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="editorial-heading text-3xl md:text-4xl text-[#A88A4A] leading-none">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-2">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/gestionale" data-testid="b2b-cta-gestionale">
                <Button size="lg" className="bg-[#C9A961] hover:bg-[#A88A4A] text-white gap-2 rounded-none px-8 h-13 text-sm uppercase tracking-[0.2em] font-bold">
                  Gestionale B2B <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/gestionale/pitch" className="inline-flex items-center gap-2 px-6 py-3 border border-stone-400 text-stone-900 hover:border-[#C9A961] hover:text-[#A88A4A] text-[10px] uppercase tracking-[0.25em] font-bold transition-colors">
                Pitch commerciale
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: MapPin, t: "San Paolo", s: "Brazil Desk operativo" },
              { icon: Ship, t: "Santos · Itajaí", s: "Porti sbarco reefer" },
              { icon: ShieldCheck, t: "MAPA · ANVISA", s: "Compliance completa" },
              { icon: Handshake, t: "DTA · ADDUMA", s: "Partner locali" },
              { icon: Languages, t: "PT-BR nativo", s: "Traduzioni & storytelling" },
              { icon: Building2, t: "10+ importatori", s: "Network consolidato" },
            ].map((c, i) => (
              <div key={i} className="border border-[#C9A961]/30 bg-white p-5 hover:border-[#C9A961] transition-colors">
                <c.icon className="h-5 w-5 text-[#A88A4A]" />
                <div className="editorial-heading text-lg text-stone-900 mt-3">{c.t}</div>
                <div className="text-xs text-stone-500 mt-1">{c.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================= PLANS ========================= */
function PlansSection() {
  const plans = [
    {
      name: "START",
      price: "€1.200",
      subprice: "all'anno · Fino a 30 referenze",
      features: [
        "Vetrina aziendale",
        "Catalogo digitale",
        "Promozione base",
        "Assistenza tecnica",
        "Dashboard analytics",
      ],
      highlight: false,
    },
    {
      name: "BUSINESS",
      badge: "Più scelto",
      price: "€2.400",
      subprice: "all'anno · Fino a 60 referenze",
      features: [
        "Tutto il piano START",
        "Promozione avanzata",
        "Supporto export base",
        "Schede prodotto premium",
        "Amazonico AI base",
      ],
      highlight: true,
    },
    {
      name: "ENTERPRISE",
      price: "€4.800",
      subprice: "all'anno · Fino a 90 referenze",
      features: [
        "Tutto il piano BUSINESS",
        "Account manager dedicato",
        "Campagne marketing custom",
        "Priorità sui nuovi mercati",
        "Amazonico AI premium",
        "Amazonico TV incluso",
      ],
      highlight: false,
    },
  ];
  return (
    <section id="piani" className="py-24 md:py-32 bg-[#faf7f0]" data-testid="b2b-plans">
      <div className="container-xl">
        <div className="mb-14">
          <h2 className="editorial-heading text-4xl md:text-6xl text-stone-900 leading-[1.05]">Piani di Affiliazione</h2>
          <div className="mt-4 h-0.5 w-24 bg-[#C9A961]" />
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((p) => (
            <div
              key={p.name}
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative bg-white shadow-md ${p.highlight ? "border-2 border-[#C9A961]" : ""}`}
            >
              {/* Header band */}
              <div className={`text-center py-6 ${p.highlight ? "bg-[#C9A961]" : "bg-[#1a1a1a]"}`}>
                <div className="editorial-heading text-3xl md:text-4xl text-white tracking-[0.15em]">{p.name}</div>
              </div>

              {/* Badge */}
              {p.badge && (
                <div className="text-center pt-6 text-[10px] uppercase tracking-[0.35em] text-[#A88A4A] font-bold">
                  {p.badge}
                </div>
              )}

              {/* Price */}
              <div className={`text-center px-8 ${p.badge ? "pt-4 pb-8" : "pt-10 pb-8"}`}>
                <div className="editorial-heading text-6xl md:text-7xl text-[#C9A961] leading-none">{p.price}</div>
                <div className="mt-6 text-sm text-stone-500">{p.subprice}</div>
              </div>

              {/* Divider */}
              <div className="mx-8 border-t border-stone-200" />

              {/* Features */}
              <ul className="px-10 py-8 space-y-5">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm text-stone-800">
                    <span className="text-[#C9A961] mt-1">·</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="px-8 pb-8">
              <Link
                to="/register"
                data-testid={`plan-cta-${p.name.toLowerCase()}`}
                className={`flex items-center justify-center gap-2 py-3.5 text-[10px] uppercase tracking-[0.3em] font-bold transition-colors ${p.highlight
                  ? "bg-[#C9A961] text-white hover:bg-[#A88A4A]"
                  : "bg-stone-900 text-white hover:bg-[#C9A961]"
                  }`}
              >
                Affiliati <ArrowRight className="h-3 w-3" />
              </Link>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-10 text-center text-xs text-stone-500 max-w-2xl mx-auto">
          Nessun costo di intermediazione sulle vendite. Solo l&apos;abbonamento annuale del piano scelto. Il ricavo delle trattative resta interamente al produttore.
        </p>
      </div>
    </section>
  );
}

/* ========================= TOOLS BAND ========================= */
function ToolsBand() {
  return (
    <section className="container-xl py-20" data-testid="b2b-tools-section">
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold mb-3">Strumenti Operativi</div>
      <h2 className="editorial-heading text-3xl md:text-5xl text-stone-900 mb-10 leading-tight">
        Il tuo spazio di lavoro <span className="italic text-[#A88A4A]">B2B</span>.
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <ToolCard to="/b2b/anagrafica" icon={Building2} title="Anagrafica" desc="Profilo azienda, licenze" testid="tool-anagrafica" />
        <ToolCard to="/b2b/lots/sell" icon={Tag} title="Vendi lotto" desc="Pubblica vini o food box" testid="tool-sell" />
        <ToolCard to="/b2b/lots/marketplace" icon={ShoppingBag} title="Acquista lotti" desc="Marketplace lotti B2B" testid="tool-buy" />
        <ToolCard to="/b2b/lots/mine" icon={ListChecks} title="I miei lotti" desc="Stato & ordini" testid="tool-mine" />
        <ToolCard to="/b2b/marketing" icon={Megaphone} title="Marketing" desc="Campagne social · TV" testid="tool-marketing" />
      </div>
    </section>
  );
}

function ToolCard({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group border border-stone-300 bg-white p-5 hover:border-[#C9A961] hover:shadow-md transition-all">
      <div className="w-10 h-10 border border-[#C9A961]/40 bg-[#C9A961]/10 flex items-center justify-center mb-3 group-hover:bg-[#C9A961] transition-colors">
        <Icon className="h-4 w-4 text-[#A88A4A] group-hover:text-white" />
      </div>
      <div className="editorial-heading text-lg text-stone-900">{title}</div>
      <div className="text-xs text-stone-500 mt-1">{desc}</div>
      <div className="mt-4 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.25em] text-[#A88A4A] font-bold group-hover:gap-2 transition-all">
        Apri <ArrowRight className="h-3 w-3" />
      </div>
    </Link>
  );
}

/* ========================= FINAL CTA ========================= */
function FinalCta() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#C9A961] via-[#e6c67e] to-[#C9A961]" />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.5) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(168,138,74,0.6) 0%, transparent 50%)" }} />
      {/* Floating decorative marquee */}
      <div className="absolute top-6 left-0 right-0 overflow-hidden pointer-events-none opacity-30">
        <div className="flex gap-8 whitespace-nowrap animate-[marquee_30s_linear_infinite]">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="editorial-heading text-6xl italic text-white/60">Made in Italy · </span>
          ))}
        </div>
      </div>
      <div className="relative container-xl text-center">
        <Globe className="h-10 w-10 text-white mx-auto mb-6" strokeWidth={1.5} />
        <h2 className="editorial-heading text-4xl md:text-7xl text-white leading-[1.05] max-w-4xl mx-auto">
          Il tuo prossimo mercato<br />
          <span className="italic">inizia da qui.</span>
        </h2>
        <p className="mt-8 text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
          Non vendiamo prodotti. Creiamo opportunità di business.
          Porta il tuo brand in Brasile e nel mondo.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <Link to="/register" data-testid="cta-final-partner">
            <Button size="lg" className="bg-white hover:bg-stone-900 text-stone-900 hover:text-white gap-2 rounded-none px-10 h-14 text-sm uppercase tracking-[0.25em] font-bold">
              <Handshake className="h-4 w-4" /> Diventa Partner
            </Button>
          </Link>
          <Link to="/contatti" data-testid="cta-final-contact">
            <Button size="lg" variant="outline" className="border-white text-white bg-transparent hover:bg-white hover:text-stone-900 gap-2 rounded-none px-10 h-14 text-sm uppercase tracking-[0.25em] font-bold">
              Richiedi demo
            </Button>
          </Link>
        </div>
        <div className="mt-12 text-[10px] uppercase tracking-[0.4em] text-white/60">
          Amazonico 3000 · ExportTech · Italia → Brasile → Mondo
        </div>
      </div>
    </section>
  );
}
